import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface ChatMessage {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: Date;
  type?: 'text' | 'quick-reply' | 'service-suggestion';
  options?: string[];
}

export interface ChatSession {
  id: string;
  messages: ChatMessage[];
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ChatBotService {
  private apiUrl = 'http://localhost:8081/api/chat'; // Spring Boot API
  private currentSession = new BehaviorSubject<ChatSession | null>(null);
  private isTyping = new BehaviorSubject<boolean>(false);

  // Base de connaissances locale (sera étendue avec l'API)
  private knowledgeBase = {
    greetings: [
      'Bonjour ! Je suis l\'assistant virtuel d\'Applisound. Comment puis-je vous aider ?',
      'Salut ! Que puis-je faire pour vous aujourd\'hui ?',
      'Hello ! Je suis là pour répondre à vos questions sur nos services.'
    ],
    services: {
      'bandes sonores': 'Nos bandes sonores sont des compositions musicales sur mesure pour vos projets vidéo, publicités ou contenus créatifs. Prix à partir de 299€.',
      'voix off': 'Nous proposons des services de voix off professionnelles avec des locuteurs natifs. Prix à partir de 149€.',
      'jingles': 'Créez votre identité sonore avec nos jingles personnalisés. Prix à partir de 199€.',
      'hymnes': 'Conception d\'hymnes originaux pour entreprises et organisations. Prix à partir de 1499€.',
      'sound design': 'Effets sonores et ambiances personnalisés pour vos productions. Prix à partir de 99€.'
    },
    faq: {
      'délai': 'Nos délais de livraison sont généralement de 3 à 10 jours ouvrés selon la complexité du projet.',
      'prix': 'Nos tarifs varient selon le service. Consultez notre page tarifs pour plus de détails.',
      'formats': 'Nous livrons en MP3, WAV, AIFF et autres formats selon vos besoins.',
      'révisions': 'Nous incluons 2 révisions gratuites dans tous nos forfaits.',
      'droits': 'Vous pouvez choisir entre une licence d\'utilisation ou une cession complète des droits.'
    }
  };

  constructor(private http: HttpClient) {
    this.initializeSession();
  }

  get currentSession$(): Observable<ChatSession | null> {
    return this.currentSession.asObservable();
  }

  get isTyping$(): Observable<boolean> {
    return this.isTyping.asObservable();
  }

  private initializeSession(): void {
    const session: ChatSession = {
      id: this.generateId(),
      messages: [],
      isActive: true
    };
    this.currentSession.next(session);
  }

  sendMessage(text: string): Observable<ChatMessage> {
    const userMessage: ChatMessage = {
      id: this.generateId(),
      text: text,
      isBot: false,
      timestamp: new Date()
    };

    this.addMessageToSession(userMessage);
    this.isTyping.next(true);

    // Simuler le délai de réponse du bot
    return this.getBotResponse(text).pipe(
      delay(1000)
    );
  }

  private getBotResponse(userMessage: string): Observable<ChatMessage> {
    const lowerMessage = userMessage.toLowerCase();
    let response = '';
    let type: 'text' | 'quick-reply' | 'service-suggestion' = 'text';
    let options: string[] = [];

    // Détection des intentions
    if (this.containsGreeting(lowerMessage)) {
      response = this.getRandomGreeting();
      type = 'quick-reply';
      options = ['Voir les services', 'Demander un devis', 'Questions fréquentes'];
    } else if (this.containsServiceQuery(lowerMessage)) {
      response = this.getServiceInfo(lowerMessage);
      type = 'service-suggestion';
      options = ['Demander un devis', 'En savoir plus', 'Autres services'];
    } else if (this.containsFAQ(lowerMessage)) {
      response = this.getFAQResponse(lowerMessage);
    } else if (this.containsPricing(lowerMessage)) {
      response = 'Nos tarifs varient selon vos besoins. Voulez-vous que je vous redirige vers notre page tarifs ou préférez-vous un devis personnalisé ?';
      type = 'quick-reply';
      options = ['Voir les tarifs', 'Demander un devis', 'Parler à un conseiller'];
    } else {
      response = 'Je ne suis pas sûr de comprendre. Pouvez-vous reformuler votre question ? Ou choisissez un sujet ci-dessous :';
      type = 'quick-reply';
      options = ['Services', 'Tarifs', 'Contact', 'FAQ'];
    }

    const botMessage: ChatMessage = {
      id: this.generateId(),
      text: response,
      isBot: true,
      timestamp: new Date(),
      type: type,
      options: options
    };

    this.addMessageToSession(botMessage);
    this.isTyping.next(false);

    return of(botMessage);
  }

  private containsGreeting(message: string): boolean {
    const greetings = ['bonjour', 'salut', 'hello', 'bonsoir', 'hey', 'coucou'];
    return greetings.some(greeting => message.includes(greeting));
  }

  private containsServiceQuery(message: string): boolean {
    return Object.keys(this.knowledgeBase.services).some(service => 
      message.includes(service)
    );
  }

  private containsFAQ(message: string): boolean {
    return Object.keys(this.knowledgeBase.faq).some(faq => 
      message.includes(faq)
    );
  }

  private containsPricing(message: string): boolean {
    const pricingKeywords = ['prix', 'tarif', 'coût', 'combien', 'budget'];
    return pricingKeywords.some(keyword => message.includes(keyword));
  }

  private getRandomGreeting(): string {
    const greetings = this.knowledgeBase.greetings;
    return greetings[Math.floor(Math.random() * greetings.length)];
  }

  private getServiceInfo(message: string): string {
    for (const [service, info] of Object.entries(this.knowledgeBase.services)) {
      if (message.includes(service)) {
        return info;
      }
    }
    return 'Nous proposons des bandes sonores, voix off, jingles, hymnes et sound design. Quel service vous intéresse ?';
  }

  private getFAQResponse(message: string): string {
    for (const [faq, answer] of Object.entries(this.knowledgeBase.faq)) {
      if (message.includes(faq)) {
        return answer;
      }
    }
    return 'Consultez notre FAQ pour plus d\'informations ou posez-moi votre question directement.';
  }

  private addMessageToSession(message: ChatMessage): void {
    const currentSession = this.currentSession.value;
    if (currentSession) {
      currentSession.messages.push(message);
      this.currentSession.next(currentSession);
    }
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Méthodes pour l'intégration future avec Spring Boot
  sendToAPI(message: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/message`, { message });
  }

  getSessionHistory(sessionId: string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(`${this.apiUrl}/session/${sessionId}`);
  }

  clearSession(): void {
    this.initializeSession();
  }
}